/**
 * PrimaryRail — top-level fixed navigation rail for global quick nav.
 *
 * A pure presentation primitive: it renders 7 fixed nav items
 * (Home / Mentorship / Conversations / Schedule / Submissions / Insights / Settings),
 * delegates all navigation logic to the caller via onSelect, and exposes no
 * internal routing or state.
 *
 * SOLID / GRASP:
 *   SRP  — chrome only; navigation is the caller's job.
 *   OCP  — new items → extend DEFAULT_PRIMARY_RAIL_ITEMS; shell unchanged.
 *   GRASP — caller is the navigation expert; rail is the visual expert.
 *
 * Responsive:
 *   ≥820px: 280px rail, icon (24px) + label + optional badge.
 *   <820px : 72px rail, icon-only; label available via aria-label.
 *
 * Accessibility:
 *   <nav role="navigation" aria-label="Primary navigation">
 *   Each item is a <button aria-current="page"> on the active item.
 *   Enter / Space activate the focused item (native button behaviour).
 */

import { type ComponentType } from 'react';
import {
    Home,
    GraduationCap,
    MessageSquare,
    Settings,
} from 'lucide-react';
import { useTelemetry } from '../telemetry/useTelemetry';
// Slice 82 (GAP-RAIL-TABTYPE-DUPLICATION): PrimaryRailKey + the
// canonical rail-item list are now derived from the TAB_METADATA SSOT
// in `core/navigation/tabMetadata.ts`. PrimaryRail.tsx keeps owning
// the visual chrome (icon map, button rendering, telemetry); the
// vocabulary (which keys, in which order, with which labels) comes
// from the SSOT. The re-exports below preserve the slice-81 public
// surface — slice 83 will point consumers directly at the SSOT.
import {
    type PrimaryRailKey,
    DEFAULT_PRIMARY_RAIL_ITEMS as SSOT_RAIL_ITEMS,
} from '../navigation/tabMetadata';
// Slice 92 (GAP-BADGE-SEMANTICS-AUDIT): per-rail badge tooltip /
// aria-label copy + display-overflow rule come from the BADGE_COUNT_RULES
// SSOT in `badgeCountRules.ts`. PrimaryRail keeps owning the visual
// chrome (icon, button, smart-count visibility); the badge VOCABULARY
// (what unit each rail counts, how the tooltip reads, the 99+ overflow
// threshold) is delegated to the SSOT so a future spec nudge edits
// ONE module. See §22.21 + §1.1.9 in the redesign doc.
import {
    BADGE_COUNT_RULES,
    formatBadgeCount,
    resolveBadgeTooltip,
} from './badgeCountRules';
import './PrimaryRail.css';

// ── ADR: dual telemetry event coexistence (slice-74 → slice-76) ────────
//
// Why both ITEM_SELECTED and ITEM_CLICK fire on the same click:
//
//   ITEM_SELECTED ('primary_rail.item_selected') — the LEGACY event. It
//   carries the full engagement payload (railKey, label, isActive,
//   collapsed, hasBadge, badgeCount) and is what existing dashboards,
//   funnels, and engagement-analysis notebooks have been subscribed to
//   since the rail shipped. Removing it now would silently break those
//   consumers mid-ramp, so it stays for the transitional window.
//
//   ITEM_CLICK ('primary_rail.item.click') — the NEW canonical event.
//   It carries the minimal PII-free routing payload (key + source) and
//   follows the dot-namespaced `workspace.deep_link.click` precedent.
//   Slice-75's ramp decision is gated on its CTR — it is the apples-to-
//   apples baseline event mirrored on both the legacy NavGroups branch
//   and the new PrimaryRail branch, so deltas are attributable to the
//   rail itself rather than to payload-shape differences.
//
// Deprecation horizon: ITEM_SELECTED will be deprecated in slice 76 once
// ITEM_CLICK has 30 days of stable data and the engagement-analysis
// dashboards have been migrated to the new shape. The dual-emission
// window keeps every consumer working through the migration — no
// dashboard goes dark for a single dogfooding cycle.
//
// Cross-reference: see §22.6 in
//   docs/redesign/mentorship-platform-redesign-2026-06-03.md
// for the full ADR (payload-shape diff, migration plan, ramp gating).
const TELEMETRY_EVENTS = {
    ITEM_SELECTED: 'primary_rail.item_selected',
    // Why: slice-74 observability ramp-prep — a stable, minimal-payload click
    // event mirrored on both branches (legacy + new rail) so slice-75's flip
    // decision has an apples-to-apples CTR baseline. No PII; routing key only.
    // Naming follows the existing workspace.deep_link.click precedent.
    ITEM_CLICK: 'primary_rail.item.click',
} as const;

// ── Public types ────────────────────────────────────────────────────────

// PrimaryRailKey is now derived from TAB_METADATA via tabMetadata.ts
// (slice 82). The union literal is preserved verbatim (same 7 keys,
// same canonical order — home/mentorship/conversations/schedule/
// submissions/insights/settings), so every existing consumer that
// imports `PrimaryRailKey` from this file keeps compiling. The
// re-export below makes the dependency direction explicit.
export type { PrimaryRailKey };

export type PrimaryRailItem = {
    key: PrimaryRailKey;
    /** Display label. Defaults to the canonical label for the key. */
    label?: string;
    /**
     * Badge count — rendered only when > 0 (smart-count rule §15.x).
     * Why: typed as optional `number` (not `number | undefined`) so
     * exactOptionalPropertyTypes distinguishes "caller omitted" from
     * "caller passed 0" — see tsconfig.app.json `exactOptionalPropertyTypes`.
     */
    badgeCount?: number;
};

export type PrimaryRailProps = {
    /** Currently active key. */
    activeKey: PrimaryRailKey;
    /** Fires when the user clicks / keyboard-activates a rail item. */
    onSelect: (key: PrimaryRailKey) => void;
    /** Optional per-item overrides (label, badge). Unmatched keys ignored. */
    items?: ReadonlyArray<PrimaryRailItem>;
    /** When true the rail shrinks to icon-only (72px). Default: false. */
    collapsed?: boolean;
    /** Root element data-testid. */
    'data-testid'?: string;
};

// ── Icon map (module-level constant) ───────────────────────────────────

// Phase 4.1: ICON_MAP shrinks alongside PrimaryRailKey (4 active rail
// keys). The schedule / submissions / insights icon imports
// (Calendar, FileText, BarChart3) become unused at this surface and
// are pruned from the import list above. Promote a rail key back to
// 'active' in FEATURE_REGISTRY and re-add the icon here when the full
// surface lands.
const ICON_MAP: Record<PrimaryRailKey, ComponentType<{ size?: number }>> = {
    home:          Home,
    mentorship:    GraduationCap,
    conversations: MessageSquare,
    settings:      Settings,
};

// ── Default items (derived from TAB_METADATA SSOT, slice 82) ───────────
//
// DEFAULT_PRIMARY_RAIL_ITEMS is now sourced from `tabMetadata.ts`. The
// derivation preserves the slice-70 canonical 7-item order and the
// slice-71 GLOSSARY routing for `submissions` + `insights` labels. The
// shape (`ReadonlyArray<{ key, label }>`) is byte-identical to the
// previous in-file declaration, so all existing tests + the rendering
// loop below keep working unchanged.
export const DEFAULT_PRIMARY_RAIL_ITEMS: ReadonlyArray<Required<Pick<PrimaryRailItem, 'key' | 'label'>>> = SSOT_RAIL_ITEMS;

// ── Resolved item shape (internal) ─────────────────────────────────────

type ResolvedItem = {
    key: PrimaryRailKey;
    label: string;
    icon: ComponentType<{ size?: number }>;
    badgeCount: number;
};

// ── Component ──────────────────────────────────────────────────────────

export function PrimaryRail({
    activeKey,
    onSelect,
    items,
    collapsed = false,
    'data-testid': testId,
}: PrimaryRailProps) {
    const { track } = useTelemetry();

    // Build a lookup of caller-supplied overrides keyed by PrimaryRailKey.
    const overrideMap = new Map<PrimaryRailKey, PrimaryRailItem>();
    if (items) {
        for (const item of items) {
            overrideMap.set(item.key, item);
        }
    }

    // Merge defaults with caller overrides. Order is always the canonical
    // 7-item sequence — callers cannot reorder the rail (KISS).
    const resolved: ResolvedItem[] = DEFAULT_PRIMARY_RAIL_ITEMS.map((def) => {
        const override = overrideMap.get(def.key);
        return {
            key:        def.key,
            label:      override?.label ?? def.label,
            icon:       ICON_MAP[def.key],
            badgeCount: override?.badgeCount ?? 0,
        };
    });

    return (
        <nav
            role="navigation"
            aria-label="Primary navigation"
            data-testid={testId}
            className={[
                'primary-rail',
                collapsed ? 'primary-rail--collapsed' : '',
            ].filter(Boolean).join(' ')}
        >
            {resolved.map((item) => {
                const isActive = item.key === activeKey;
                const Icon = item.icon;
                return (
                    <button
                        key={item.key}
                        type="button"
                        className={[
                            'primary-rail__item',
                            isActive ? 'is-active' : '',
                        ].filter(Boolean).join(' ')}
                        aria-current={isActive ? 'page' : undefined}
                        aria-label={collapsed ? item.label : undefined}
                        onClick={() => {
                            // Slice-74: emit the minimal-payload ramp-baseline
                            // event BEFORE invoking onSelect so the click is
                            // counted even if the caller's handler throws.
                            // Payload contains routing key + source only — no PII.
                            track(TELEMETRY_EVENTS.ITEM_CLICK, {
                                key: item.key,
                                source: 'rail',
                            });
                            track(TELEMETRY_EVENTS.ITEM_SELECTED, {
                                railKey: item.key,
                                label: item.label,
                                isActive,
                                collapsed,
                                hasBadge: item.badgeCount > 0,
                                badgeCount: item.badgeCount,
                            });
                            onSelect(item.key);
                        }}
                        data-key={item.key}
                    >
                        <span className="primary-rail__icon" aria-hidden="true">
                            <Icon size={24} />
                        </span>
                        {!collapsed && (
                            <span className="primary-rail__label">{item.label}</span>
                        )}
                        {item.badgeCount > 0 && (
                            <span
                                className="primary-rail__badge"
                                // Slice 92: tooltip / aria-label copy comes
                                // from the BADGE_COUNT_RULES SSOT so the
                                // unit ("submissions", "threads", "items")
                                // matches the rail's audited semantic per
                                // §1.1.9. No magic strings here — a future
                                // copy nudge edits `badgeCountRules.ts`.
                                aria-label={resolveBadgeTooltip(item.key, item.badgeCount)}
                                title={resolveBadgeTooltip(item.key, item.badgeCount)}
                            >
                                {formatBadgeCount(item.badgeCount, BADGE_COUNT_RULES[item.key])}
                            </span>
                        )}
                    </button>
                );
            })}
        </nav>
    );
}

export default PrimaryRail;
